import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import s from './Modal.module.css';
import exitIcon from '../../../assets/img/icons/exit-cross-icon.svg';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

type ModalSize = 'sm' | 'md' | 'lg' | 'fullscreen';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Renders an h2 title and wires aria-labelledby. */
  title?: string;
  size?: ModalSize;
  /** Close when clicking the backdrop. Defaults to true. */
  closeOnOverlayClick?: boolean;
  /** Show the floating × close button. Defaults to true. */
  showCloseBtn?: boolean;
};

/**
 * Accessible modal primitive.
 *
 * - Renders via createPortal to document.body
 * - Traps focus (Tab / Shift+Tab) within the dialog
 * - Closes on Escape key
 * - Locks body scroll while open
 * - Sets role="dialog" and aria-modal="true"
 */
export const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  size = 'md',
  closeOnOverlayClick = true,
  showCloseBtn = true,
}: ModalProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Defer focus so the element is painted before we try to focus it.
    const rafId = requestAnimationFrame(() => {
      const focusable =
        containerRef.current?.querySelectorAll<HTMLElement>(
          FOCUSABLE_SELECTORS
        );
      focusable?.[0]?.focus();
    });
    return () => {
      cancelAnimationFrame(rafId);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable =
      containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
    if (!focusable?.length) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={s.overlay}
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={`${s.container} ${s[size]}`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {showCloseBtn && (
          <button
            type="button"
            className={s.closeBtn}
            onClick={onClose}
            aria-label="Закрити"
          >
            <img src={exitIcon} alt="" aria-hidden="true" />
          </button>
        )}
        {title && (
          <h2 id={titleId} className={s.title}>
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};
